/**
 * Toast Notification Utilities
 * Wrapper around react-hot-toast for consistent toast notifications.
 */

import hotToast, { type ToastOptions } from 'react-hot-toast';

/**
 * Toast configuration defaults.
 */
const defaultOptions: ToastOptions = {
  duration: 4000,
  position: 'top-right',
};

/**
 * Success toast default options.
 */
const successOptions: ToastOptions = {
  ...defaultOptions,
  duration: 3000,
  style: {
    background: '#10B981',
    color: '#fff',
  },
  iconTheme: {
    primary: '#fff',
    secondary: '#10B981',
  },
};

/**
 * Error toast default options.
 */
const errorOptions: ToastOptions = {
  ...defaultOptions,
  duration: 5000,
  style: {
    background: '#EF4444',
    color: '#fff',
  },
  iconTheme: {
    primary: '#fff',
    secondary: '#EF4444',
  },
};

/**
 * Warning toast default options.
 */
const warningOptions: ToastOptions = {
  ...defaultOptions,
  duration: 4000,
  style: {
    background: '#F59E0B',
    color: '#fff',
  },
  iconTheme: {
    primary: '#fff',
    secondary: '#F59E0B',
  },
};

/**
 * Info toast default options.
 */
const infoOptions: ToastOptions = {
  ...defaultOptions,
  duration: 4000,
  style: {
    background: '#3B82F6',
    color: '#fff',
  },
  iconTheme: {
    primary: '#fff',
    secondary: '#3B82F6',
  },
};

/**
 * Toast utility object with methods for different toast types.
 */
export const toast = {
  /**
   * Shows a success toast.
   */
  success: (message: string, options?: ToastOptions): string => {
    return hotToast.success(message, { ...successOptions, ...options });
  },

  /**
   * Shows an error toast.
   */
  error: (message: string, options?: ToastOptions): string => {
    return hotToast.error(message, { ...errorOptions, ...options });
  },

  /**
   * Shows a warning toast.
   */
  warning: (message: string, options?: ToastOptions): string => {
    return hotToast(message, {
      ...warningOptions,
      ...options,
      icon: '⚠️',
    });
  },

  /**
   * Shows an info toast.
   */
  info: (message: string, options?: ToastOptions): string => {
    return hotToast(message, {
      ...infoOptions,
      ...options,
      icon: 'ℹ️',
    });
  },

  /**
   * Shows a loading toast and returns functions to update it.
   */
  loading: (message: string, options?: ToastOptions): string => {
    return hotToast.loading(message, { ...defaultOptions, ...options });
  },

  /**
   * Updates an existing toast.
   */
  update: (toastId: string, message: string, type: 'success' | 'error' | 'loading'): void => {
    if (type === 'success') {
      hotToast.success(message, { id: toastId, ...successOptions });
    } else if (type === 'error') {
      hotToast.error(message, { id: toastId, ...errorOptions });
    } else {
      hotToast.loading(message, { id: toastId });
    }
  },

  /**
   * Dismisses a toast by ID.
   */
  dismiss: (toastId?: string): void => {
    hotToast.dismiss(toastId);
  },

  /**
   * Dismisses all toasts.
   */
  dismissAll: (): void => {
    hotToast.dismiss();
  },

  /**
   * Shows a promise toast that updates based on promise state.
   */
  promise: <T>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((err: unknown) => string);
    },
    options?: ToastOptions
  ): Promise<T> => {
    return hotToast.promise(promise, messages, { ...defaultOptions, ...options });
  },

  /**
   * Shows a custom toast with JSX content.
   */
  custom: (content: React.ReactNode, options?: ToastOptions): string => {
    return hotToast.custom(() => content, { ...defaultOptions, ...options });
  },
};

export default toast;

